import { useLayoutEffect, useRef, useState } from "react";
import {
  TrendingUp, Store, AlertCircle, FileText, ShoppingCart, Clock, Truck, CheckCircle2, Ban, Plus, Minus,
  ChevronLeft, ChevronRight, ChevronDown, MousePointer2, Search, X, RefreshCw, SlidersHorizontal, User,
  LayoutDashboard, Receipt, Landmark, ChefHat, UtensilsCrossed, Banknote, ArrowUpDown, Soup,
  Utensils, Coffee, Leaf, Candy, FileDown, MapPin, Globe, Camera, Pencil, Trash2, Tag, CalendarRange,
  Eye, Save,
} from "lucide-react";
import { useScenar, usePrelinanie, useMierka, polohaV, Pocitadlo, euraBodka, kusy } from "../scenar";
import Kapitoly from "./Kapitoly";
import "./UkazkaRestauracia.scss";

/**
 * Ukazka "Restauracia" v sekcii Ako to funguje: okno admin panelu
 * (MANAGER PANEL) a v nom pribeh jedneho dna:
 *   1. Sprava jedal -- "Pridat jedlo" (nazov, popis, alergeny), jedlo je v zozname,
 *   2. Sprava obedoveho menu -- "Zostavit tyzdenne menu", na stredu "+ Pridat",
 *      vyber jedla (kod 3 a cenu predvyplni aplikacia: hlavne jedla maju cisla,
 *      polievky pismena -- nextCode v DailyMenuSection), "Ulozit menu",
 *   3. Dashboard -- "Nova objednavka" (OrderModal: zakaznik, jedla; trasu
 *      predvyplni adresa), objednavka BOD pribudne medzi Prijate,
 *   4. stavove tlacidlo "Na trase" (dopredu bez potvrdenia),
 *   5. Prehlad -- zive obnovenie: pride objednavka z webu (grafy), vodic
 *      doruci BOD na dlh (trzba aj Neuhradene / Dlh), v zozname je Dorucena,
 *   6. Sprava dlhov -- "Prijat platbu" -> "Zaevidovat platbu", dlznik zmizne.
 *
 * Kopiruje skutocne obrazovky (MenuItemsSection, DailyMenuSection,
 * DashboardSection, OrderModal, OverviewSection, DebtManagementSection,
 * DebtPaymentModal) vratane popisov a formatu sum
 * (Prehlad a Dashboard toFixed(2) s bodkou, dlhy s ciarkou a so znamienkom).
 * Vynechane je len to, co by v malom okne zavadzalo (napr. alert po zaevidovani
 * platby). Mena, adresy a sumy su vymyslene.
 *
 * Scena je navrhnuta na SIRKA x VYSKA px a zmensuje sa mierkou, aby sedela aj
 * na mobile. Kurzor sa meria cez offsetParent, takze mierka ho nerozhodi.
 */

const SIRKA = 960;
const VYSKA = 580;

const KROKY = [
  // Sprava jedal
  ["start", 0],
  ["kPridat", 900],
  ["klikPridat", 1600],
  ["jedloModal", 1800],
  ["nazov", 2400],
  ["popis", 3000],
  ["kAlergen1", 3500],
  ["alergen1", 4200],
  ["kAlergen7", 4500],
  ["alergen7", 5200],
  ["kUlozitJedlo", 5600],
  ["ulozitJedlo", 6300],
  ["jedloUlozene", 6500],  // nove jedlo v zozname
  // Sprava obedoveho menu
  ["kMenu", 7600],
  ["menu", 8400],
  ["kZostavit", 9200],
  ["klikZostavit", 9900],
  ["builder", 10100],
  ["kPridatDen", 10900],
  ["klikPridatDen", 11600],
  ["picker", 11800],
  ["hladaj", 12400],
  ["kHalusky", 12900],
  ["halusky", 13600],      // jedlo pribudne na stredu: kod 3, cena podla posledneho riadku
  ["kUlozitMenu", 14400],
  ["ulozitMenu", 15100],
  ["ulozeneMenu", 15500],
  ["kZavriet", 16400],
  ["zavriet", 17100],
  // Dashboard
  ["kDashboard", 18200],
  ["dashboard", 19000],
  ["kNova", 19800],
  ["klikNova", 20600],
  ["modal", 20800],
  ["pis", 21400],          // dispecer pise meno
  ["vysledky", 21900],
  ["kZakaznik", 22100],
  ["zakaznik", 22800],     // vybraty -- adresa a trasa sa predvyplnia
  ["kA", 23300],
  ["klikA", 24000],
  ["kB", 24400],
  ["klikB", 25100],
  ["kZadat", 25600],
  ["zadat", 26300],
  ["ulozene", 26500],      // BOD pribudne na Dashboarde
  ["kNaTrase", 27300],
  ["naTrase", 28100],
  // Prehlad
  ["kPrehlad", 30000],
  ["prehlad", 30800],
  ["webova", 32200],       // zivo: objednavka z webu -- grafy sa pohnu
  ["dorucene", 34400],     // zivo: vodic doruci BOD na dlh
  ["dole", 36400],         // pohlad na zoznam objednavok
  // Sprava dlhov
  ["kDlhy", 39200],
  ["dlhy", 40000],
  ["kPrijat", 41000],
  ["klikPrijat", 41800],
  ["platbaModal", 42000],
  ["kZaevidovat", 43100],
  ["zaevidovat", 43900],
  ["vyplatene", 44100],    // dlznik zmizne zo zoznamu
  ["koniec", 48000],       // posledny zaber -- do noveho kola sa prelne
];
const DLZKA_KOLA = 48600;

const KAPITOLY = [
  { nazov: "Nové jedlo", krok: "start" },
  { nazov: "Obedové menu", krok: "kMenu" },
  { nazov: "Nová objednávka", krok: "kDashboard" },
  { nazov: "Na trasu", krok: "kNaTrase" },
  { nazov: "Prehľad a doručenie", krok: "kPrehlad" },
  { nazov: "Vyplatenie dlhu", krok: "kDlhy" },
];

// Stavy ako ORDER_STATUS_META
const STAVY = [
  { kluc: "ACCEPTED", popis: "Prijatá", Icon: Clock, mod: "prijata" },
  { kluc: "DELIVERY", popis: "Na trase", Icon: Truck, mod: "na-trase" },
  { kluc: "DELIVERED", popis: "Doručená", Icon: CheckCircle2, mod: "dorucena" },
  { kluc: "CANCELLED", popis: "Zrušená", Icon: Ban, mod: "zrusena" },
];
const stav = (kluc) => STAVY.find((s) => s.kluc === kluc);

// Farby trás v ukážke = údaje (v aplikácii routes.color_code), nie štýl.
// rgb pre priehľadný podklad štítka — rgba(), nie color-mix() kvôli html2canvas.
const TRASY = {
  SEVER: { farba: "#2563EB", rgb: "37, 99, 235" }, // farba-z-dat
  JUH: { farba: "#EA580C", rgb: "234, 88, 12" }, // farba-z-dat
};

const BOD = {
  kod: "BOD", adresa: "Bodová 8, Košice", meno: "Eva Bellová", telefon: "+421 948 222 333",
  cena: 14.1, jedla: [["1x", "1"], ["1x", "2"]], trasa: "SEVER", cas: "10:41",
};
const STV = {
  kod: "STV", adresa: "Hlavná 30, Košice", meno: "Ján Horák", telefon: "+421 905 123 456",
  cena: 8.4, jedla: [["1x", "A"], ["1x", "3"]], trasa: "SEVER", cas: "10:38",
};
const OBU = {
  kod: "OBU", adresa: "Námestie 3, Košice", meno: "Obecný úrad", telefon: "+421 911 654 321",
  cena: 14.1, jedla: [["1x", "2"], ["1x", "3"]], trasa: "JUH", cas: "10:12",
};
const DOM = {
  kod: "DOM", adresa: "Domová 15, Košice", meno: "Peter Domanský", telefon: "+421 902 777 100",
  cena: 14.1, jedla: [["1x", "3"], ["1x", "2"]], trasa: "JUH", cas: "09:20",
};
const NOV = {
  kod: "NOV", adresa: "Novákova 5, Košice", meno: "Lucia Nováková", telefon: "+421 917 300 200",
  cena: 6.9, jedla: [["1x", "1"]], trasa: "JUH", cas: "10:44", web: true,
};

const MENU_POLOZKY = [
  { kod: "1", nazov: "Kurací rezeň, zemiaková kaša", cena: 6.9 },
  { kod: "2", nazov: "Hovädzí guláš, knedľa", cena: 7.2 },
  { kod: "3", nazov: "Zeleninové rizoto", cena: 6.9 },
];

// Sprava jedal -- katalog (bez cien: tie su az v obedovom menu)
const JEDLA = [
  { typ: "Polievka", Icon: Soup, nazov: "Paradajková polievka", popis: "s cestovinou", foto: true, alergeny: [1], tagy: [] },
  { typ: "Hlavné jedlo", Icon: Utensils, nazov: "Kurací rezeň", popis: "zemiaková kaša, uhorkový šalát", foto: true, alergeny: [1, 3, 7], tagy: ["obľúbené"] },
  { typ: "Hlavné jedlo", Icon: Utensils, nazov: "Hovädzí guláš", popis: "knedľa", foto: false, alergeny: [1, 3], tagy: [] },
  { typ: "Hlavné jedlo", Icon: Utensils, nazov: "Zeleninové rizoto", popis: "parmezán", foto: true, alergeny: [7], tagy: ["vegetariánske"] },
  { typ: "Polievka", Icon: Soup, nazov: "Fazuľová polievka", popis: "s údeným mäsom", foto: false, alergeny: [], tagy: [] },
  { typ: "Dezert", Icon: Candy, nazov: "Palacinky", popis: "s tvarohom", foto: false, alergeny: [1, 3, 7], tagy: [] },
];
const NOVE_JEDLO = {
  typ: "Hlavné jedlo", Icon: Utensils, nazov: "Bryndzové halušky", popis: "so slaninou", foto: false, alergeny: [1, 7], tagy: [],
};
const POPULARNE_TAGY = ["obľúbené", "vegetariánske", "bezlepkové", "pikantné"];

// ALLERGENS z orderConstants.js
const ALERGENY = [
  [1, "Obilniny obsahujúce lepok (pšenica, raž, jačmeň, ovos...)"],
  [2, "Kôrovce a výrobky z nich"],
  [3, "Vajcia a výrobky z nich"],
  [4, "Ryby a výrobky z nich"],
  [5, "Arašidy a výrobky z nich"],
  [6, "Sójové zrná a výrobky z nich"],
  [7, "Mlieko a výrobky z neho"],
  [8, "Orechy (mandle, lieskovce, vlašské, kešu, pekanové...)"],
  [9, "Zeler a výrobky z neho"],
  [10, "Horčica a výrobky z nej"],
  [11, "Sezamové semená a výrobky z nich"],
  [12, "Oxid siričitý a siričitany (>10 mg/kg alebo mg/l)"],
  [13, "Vlčí bôb a výrobky z neho"],
  [14, "Mäkkýše a výrobky z nich"],
];

const BOCNE_MENU = [
  { Icon: LayoutDashboard, popis: "Dashboard", kluc: "dashboard" },
  { Icon: Receipt, popis: "Prehľad", kluc: "prehlad" },
  { Icon: Landmark, popis: "Správa dlhov", kluc: "dlhy" },
  { Icon: ChefHat, popis: "Správa jedál", kluc: "jedla" },
  { Icon: UtensilsCrossed, popis: "Správa obedového menu", kluc: "menu" },
];

const Jedla = ({ polozky }) => (
  <span className="ur__meals">
    {polozky.map(([pocet, kod]) => (
      <span key={kod}>{pocet} <b>{kod}</b></span>
    ))}
  </span>
);

const Trasa = ({ nazov }) => (
  <span className="ur__route" style={{ "--farba": TRASY[nazov].farba, "--farba-rgb": TRASY[nazov].rgb }}>
    <i /> {nazov}
  </span>
);

/** Obal, ktory obsah plynulo rozbali / zbali. */
const Skladacka = ({ otvorena, children }) => (
  <div className={`ur__fold ${otvorena ? "" : "ur__fold--zatvorena"}`}>
    <div>{children}</div>
  </div>
);

const HlavickaKarty = ({ o, cas }) => (
  <div className="ur__card-head">
    <span className="ur__code">{o.kod}</span>
    <span className="ur__addr">{o.adresa} <MapPin size={10} /></span>
    <span className="ur__card-right">
      {cas && <span className="ur__created">13.10.2026 <b>{o.cas}</b></span>}
      <span className="ur__tag">ŠTANDARD</span>
      <strong>{o.cena.toFixed(2)} €</strong>
    </span>
  </div>
);

const ZakaznikKarty = ({ o }) => (
  <div className="ur__customer">
    <b>{o.meno}</b>
    {o.web && <span className="ur__badge-web"><Globe size={9} /> WEB</span>}
    <span className="ur__phone">{o.telefon}</span>
  </div>
);

/** Riadok tabulky v Sprave jedal. */
const RiadokJedla = ({ j }) => (
  <div className="ur__tr ur__tr--jedla">
    <span className="ur__typ"><j.Icon size={12} /> {j.typ}</span>
    <b>{j.nazov}</b>
    <span className="ur__muted ur__ellipsis">{j.popis}</span>
    <span>{j.foto ? <Camera size={13} className="ur__foto" /> : <span className="ur__muted">—</span>}</span>
    <span className="ur__pills">
      {j.alergeny.length ? j.alergeny.map((n) => <i key={n} className="ur__alergen-pill">{n}</i>) : <span className="ur__muted">—</span>}
    </span>
    <span className="ur__pills">
      {j.tagy.length ? j.tagy.map((t) => <i key={t} className="ur__tag-chip">{t}</i>) : <span className="ur__muted">—</span>}
    </span>
    <span className="ur__row-actions"><Pencil size={12} /><Trash2 size={12} className="ur__zmazat" /></span>
  </div>
);

/** Polozka dna v Sprave obedoveho menu (BoardItemCard). */
const PolozkaDna = ({ kod, Icon, nazov, popis, cena }) => (
  <div className="ur__board-item">
    <b className="ur__code ur__code--menu">{kod}</b>
    <Icon size={12} className="ur__muted" />
    <span className="ur__board-name"><b>{nazov}</b> <span className="ur__muted">{popis}</span></span>
    <b>{cena}</b>
    <span className="ur__eye"><Eye size={12} /></span>
  </div>
);

/** Riadok jedla v dni pri zostavovani menu (EntryRow). */
const RiadokMenu = ({ kod, nazov, cena, nove }) => (
  <div className={`ur__entry ${nove ? "ur__entry--nove" : ""}`}>
    <span className="ur__entry-kod">{kod}</span>
    <span className="ur__entry-nazov">{nazov}</span>
    <span className="ur__entry-cena">{cena}</span>
    <Trash2 size={12} className="ur__zmazat" />
  </div>
);

/** Karta na Dashboarde -- stav sa meni tlacidlami na karte. */
const KartaDashboard = ({ o, stavKluc, platba, naTraseRef, stlacene }) => (
  <div className="ur__card">
    <HlavickaKarty o={o} />
    <div className="ur__card-body">
      <ZakaznikKarty o={o} />
      <Jedla polozky={o.jedla} />
      <div className="ur__actions">
        <div className="ur__states">
          {STAVY.map(({ kluc, popis, Icon, mod }) => (
            <span
              key={kluc}
              ref={kluc === "DELIVERY" ? naTraseRef : undefined}
              className={[
                "ur__state",
                `ur__state--${mod}`,
                kluc === stavKluc ? "ur__state--aktivny" : "",
                kluc === "DELIVERY" && stlacene ? "ur__state--stlacene" : "",
              ].join(" ")}
            >
              <Icon size={10} /> {popis}
            </span>
          ))}
        </div>
        <Trasa nazov={o.trasa} />
      </div>
    </div>
    {platba && (
      <div className="ur__payment"><span>Platba:</span> <Banknote size={11} /> {platba}</div>
    )}
  </div>
);

/** Karta v Prehlade -- len na citanie, so stitkami Stav / Trasa / Platba. */
const KartaPrehlad = ({ o, stavKluc, platba, className = "" }) => {
  const { popis, Icon, mod } = stav(stavKluc);
  return (
    <div className={`ur__card ${className}`}>
      <HlavickaKarty o={o} cas />
      <div className="ur__card-body">
        <ZakaznikKarty o={o} />
        <Jedla polozky={o.jedla} />
        <div className="ur__labels">
          <span><small>Stav:</small><span className={`ur__status ur__status--${mod}`}><Icon size={10} /> {popis}</span></span>
          <span><small>Trasa:</small><Trasa nazov={o.trasa} /></span>
          {platba && <span><small>Platba:</small><span className="ur__pay-badge">{platba}</span></span>}
        </div>
      </div>
    </div>
  );
};

/**
 * Obsah okna (bocne menu + obrazovky) v zabere, ktory urcuje `po`. Pri
 * prelinani sa vykresli druhy raz pre predosly zaber -- ten nedostane refy
 * (`ciele`, `prehladRef`, `zoznamRef`), aby neprepisal tie, ktore meria kurzor.
 */
function Aplikacia({ po, ciele = {}, prehladRef, zoznamRef, posun = 0, className = "" }) {
  const obrazovka =
    po("dlhy") ? "dlhy" :
    po("prehlad") ? "prehlad" :
    po("dashboard") ? "dashboard" :
    po("menu") ? "menu" : "jedla";
  const modalJedlo = po("jedloModal") && !po("jedloUlozene");
  const modalMenu = po("builder") && !po("zavriet");
  const pickerOtvoreny = po("picker") && !po("halusky");
  const menuUlozene = po("ulozeneMenu");
  const ulozene = po("ulozene");
  const naTrase = po("naTrase");
  const webova = po("webova");
  const dorucene = po("dorucene");
  const modalObjednavka = po("modal") && !ulozene;
  const modalPlatba = po("platbaModal") && !po("vyplatene");

  const qtyA = po("klikA") ? 1 : 0;
  const qtyB = po("klikB") ? 1 : 0;
  const medzisucet = qtyA * 6.9 + qtyB * 7.2;

  const kpi = [
    { Icon: TrendingUp, hodnota: dorucene ? 262.4 : 248.3, popis: "Tržba z rozvozu (cash)", mod: "hlavna", zmena: dorucene },
    { Icon: Store, hodnota: 41.4, ks: 6, popis: "Tržba na prevádzke" },
    { Icon: AlertCircle, hodnota: dorucene ? 27.9 : 13.8, ks: dorucene ? 3 : 2, popis: "Neuhradené / Dlh", mod: "dlh", zmena: dorucene },
    { Icon: FileText, hodnota: 69, ks: 10, popis: "Na faktúru" },
    { Icon: ShoppingCart, pocet: webova ? 44 : 43, popis: "Počet objednávok", zmena: webova },
  ];

  const predajnost = [
    { kod: "1", nazov: "Kurací rezeň, zemiaková kaša", ks: webova ? 16 : 15, limit: 30, cena: 6.9, zmena: webova },
    { kod: "2", nazov: "Hovädzí guláš, knedľa", ks: 12, limit: 25, cena: 7.2 },
    { kod: "3", nazov: "Zeleninové rizoto", ks: 9, limit: 20, cena: 6.9 },
  ];
  const hodiny = [
    { h: "08:00", obj: 5, eur: 36 },
    { h: "09:00", obj: 14, eur: 101 },
    { h: "10:00", obj: webova ? 18 : 17, eur: webova ? 129 : 122, zmena: webova },
    { h: "11:00", obj: 7, eur: 50 },
  ];
  const maxObj = Math.max(...hodiny.map((d) => d.obj));

  const dlznici = [
    { meno: "Peter", priezvisko: "Domanský", tel: "+421 902 777 100", kod: "DOM", adresa: "Domová 15, Košice", dlh: "-6,40 €" },
    { meno: "Mária", priezvisko: "Kováčová", tel: "+421 900 123 456", kod: "KOV", adresa: "Hlavná 12, Košice", dlh: "-12,40 €" },
    { meno: "Eva", priezvisko: "Bellová", tel: "+421 948 222 333", kod: "BOD", adresa: "Bodová 8, Košice", dlh: "-14,10 €", ona: true },
    { meno: "Ján", priezvisko: "Horák", tel: "+421 905 123 456", kod: "STV", adresa: "Hlavná 30, Košice", dlh: "-18,90 €" },
  ];

  return (
    <div className={`ur__app ${className}`}>
      <nav className="ur__side">
        <div className="ur__side-title">MANAGER PANEL</div>
        {BOCNE_MENU.map(({ Icon, popis, kluc }) => (
          <span
            key={popis}
            ref={{
              dashboard: ciele.menuDashboard, prehlad: ciele.menuPrehlad, dlhy: ciele.menuDlhy, menu: ciele.menuMenu,
            }[kluc]}
            className={`ur__side-item ${kluc === obrazovka ? "ur__side-item--aktivna" : ""}`}
          >
            <Icon size={15} /> {popis}
          </span>
        ))}
      </nav>

      <div className="ur__content">
        {/* ============ SPRAVA JEDAL ============ */}
        <div className={`ur__screen ${obrazovka === "jedla" ? "ur__screen--aktivna" : ""}`}>
          <div className="ur__screen-head">
            <h2 className="ur__h1">Správa jedál</h2>
            <span ref={ciele.pridatJedlo} className={`ur__btn ur__btn--primary ${po("klikPridat") && !po("jedloModal") ? "ur__btn--stlacene" : ""}`}>
              <Plus size={13} /> Pridať jedlo
            </span>
          </div>
          <div className="ur__tagfilter">
            <span className="ur__search ur__search--tag"><Search size={12} /> Filtrovať podľa tagu...</span>
            <span className="ur__muted">Populárne:</span>
            {POPULARNE_TAGY.map((t) => <i key={t} className="ur__tag-chip">{t}</i>)}
          </div>
          <div className="ur__panel">
            <div className="ur__selects">
              <span className="ur__search"><Search size={12} /> Hľadať jedlo...</span>
              <span className="ur__select">Všetky typy <ChevronDown size={11} /></span>
              <span className="ur__muted ur__page-size">Zobraz <span className="ur__select">10 <ChevronDown size={11} /></span> riadkov</span>
            </div>
            <div className="ur__tr ur__tr--jedla ur__th">
              <span>Typ <ArrowUpDown size={10} /></span><span>Názov <ArrowUpDown size={10} /></span><span>Popis <ArrowUpDown size={10} /></span>
              <span>Foto</span><span>Alergény <ArrowUpDown size={10} /></span><span>Tagy <ArrowUpDown size={10} /></span><span>Akcie</span>
            </div>
            <Skladacka otvorena={po("jedloUlozene")}>
              <div className={po("jedloUlozene") ? "ur__nove" : ""}><RiadokJedla j={NOVE_JEDLO} /></div>
            </Skladacka>
            {JEDLA.map((j) => <RiadokJedla key={j.nazov} j={j} />)}
          </div>

          {/* modal Pridat jedlo */}
          <div className={`ur__overlay ${modalJedlo ? "ur__overlay--tu" : ""}`}>
            <div className="ur__modal ur__modal--jedlo">
              <div className="ur__modal-title">Pridať jedlo <X size={15} /></div>
              <div className="ur__jedlo-grid">
                <div>
                  <label className="ur__label">Typ jedla</label>
                  <div className="ur__input ur__input--neutral ur__input--select"><span><Utensils size={12} /> Hlavné jedlo</span><ChevronDown size={12} /></div>
                  <label className="ur__label">Názov</label>
                  <div className={`ur__input ${po("popis") ? "ur__input--neutral" : ""}`}>
                    {po("nazov") ? NOVE_JEDLO.nazov : <span className="ur__placeholder">napr. Cesnaková polievka</span>}
                    {!po("popis") && <i className="ur__caret" />}
                  </div>
                  <label className="ur__label">Popis</label>
                  <div className={`ur__input ur__input--area ${po("popis") && !po("kAlergen1") ? "" : "ur__input--neutral"}`}>
                    {po("popis") ? NOVE_JEDLO.popis : <span className="ur__placeholder">Krátky popis jedla</span>}
                    {po("popis") && !po("kAlergen1") && <i className="ur__caret" />}
                  </div>
                  <label className="ur__label">Fotografia</label>
                  <span className="ur__btn ur__btn--outline ur__btn--male"><Camera size={12} /> Vybrať súbor</span>
                </div>
                <div>
                  <label className="ur__label"><Tag size={11} /> Tagy</label>
                  <div className="ur__input ur__input--neutral"><span className="ur__placeholder">Napíš tag a stlač Enter...</span></div>
                  <div className="ur__tagfilter ur__tagfilter--modal">
                    <span className="ur__muted">Populárne:</span>
                    {POPULARNE_TAGY.map((t) => <i key={t} className="ur__tag-chip">{t}</i>)}
                  </div>
                  <label className="ur__label">Alergény</label>
                  <div className="ur__alergeny">
                    {ALERGENY.map(([n, text]) => {
                      const zaskrtnute = (n === 1 && po("alergen1")) || (n === 7 && po("alergen7"));
                      return (
                        <span
                          key={n}
                          ref={n === 1 ? ciele.alergen1 : n === 7 ? ciele.alergen7 : undefined}
                          className="ur__alergen"
                        >
                          <i className={`ur__checkbox ${zaskrtnute ? "ur__checkbox--on" : ""}`} />
                          <span><b>{n}.</b> {text}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="ur__modal-buttons ur__modal-buttons--ram">
                <span className="ur__btn ur__btn--outline">Zrušiť</span>
                <span ref={ciele.ulozitJedlo} className={`ur__btn ur__btn--primary ${po("ulozitJedlo") ? "ur__btn--stlacene" : ""}`}>
                  Pridať jedlo
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ============ SPRAVA OBEDOVEHO MENU ============ */}
        <div className={`ur__screen ${obrazovka === "menu" ? "ur__screen--aktivna" : ""}`}>
          <div className="ur__screen-head">
            <h2 className="ur__h1">Správa obedového menu</h2>
            <span ref={ciele.zostavit} className={`ur__btn ur__btn--primary ${po("klikZostavit") && !po("builder") ? "ur__btn--stlacene" : ""}`}>
              <CalendarRange size={13} /> Zostaviť týždenné menu
            </span>
          </div>
          <div className="ur__filter">
            <span className="ur__nav"><ChevronLeft size={14} /></span>
            <span className="ur__field"><small>Od</small>12.10.2026</span>
            <span className="ur__muted">–</span>
            <span className="ur__field"><small>Do</small>18.10.2026</span>
            <span className="ur__nav"><ChevronRight size={14} /></span>
          </div>

          <div className="ur__day">
            <div className="ur__day-head"><b>Pondelok</b><span className="ur__muted">12.10.2026</span><span className="ur__day-count">5</span><ChevronDown size={14} className="ur__muted" /></div>
          </div>
          <div className="ur__day ur__day--dnes">
            <div className="ur__day-head">
              <b>Utorok</b><span className="ur__muted">13.10.2026</span><span className="ur__dnes">Dnes</span>
              <span className="ur__day-count">4</span><ChevronDown size={14} className="ur__muted ur__otocena" />
            </div>
            <div className="ur__day-body">
              <div className="ur__day-cat"><Soup size={12} /> Polievky</div>
              <PolozkaDna kod="A" Icon={Soup} nazov="Paradajková polievka" popis="s cestovinou" cena="1.50 €" />
              <div className="ur__day-cat"><Utensils size={12} /> Hlavné jedlá</div>
              <PolozkaDna kod="1" Icon={Utensils} nazov="Kurací rezeň" popis="zemiaková kaša, uhorkový šalát" cena="6.90 €" />
              <PolozkaDna kod="2" Icon={Utensils} nazov="Hovädzí guláš" popis="knedľa" cena="7.20 €" />
              <PolozkaDna kod="3" Icon={Utensils} nazov="Zeleninové rizoto" popis="parmezán" cena="6.90 €" />
            </div>
          </div>
          <div className="ur__day">
            <div className="ur__day-head">
              <b>Streda</b><span className="ur__muted">14.10.2026</span>
              <span className={`ur__day-count ${menuUlozene ? "ur__zmena" : ""}`} key={menuUlozene ? 4 : 3}>{menuUlozene ? 4 : 3}</span>
              <ChevronDown size={14} className="ur__muted" />
            </div>
          </div>
          <div className="ur__day">
            <div className="ur__day-head"><b>Štvrtok</b><span className="ur__muted">15.10.2026</span><span className="ur__day-count">4</span><ChevronDown size={14} className="ur__muted" /></div>
          </div>

          {/* Zostavenie tyzdenneho menu */}
          <div className={`ur__overlay ${modalMenu ? "ur__overlay--tu" : ""}`}>
            <div className="ur__modal ur__modal--menu">
              <div className="ur__modal-title">Zostavenie týždenného menu <X size={15} /></div>
              <div className="ur__builder-top">
                <span className="ur__field ur__field--siroke"><small>Vyberte týždeň (ľubovoľný deň)</small>13.10.2026</span>
                <b>12.10.2026 – 18.10.2026</b>
              </div>
              <div className="ur__builder-body">
                <div className="ur__dsec">
                  <div className="ur__dsec-head">
                    <b>Streda&nbsp;&nbsp;•&nbsp;&nbsp;14.10.2026</b>
                    <span ref={ciele.pridatDen} className={`ur__btn ur__btn--outline ur__btn--male ${po("klikPridatDen") && !po("picker") ? "ur__btn--stlacene" : ""}`}>
                      <Plus size={11} /> Pridať
                    </span>
                  </div>
                  {/* ItemPicker -- rozbali sa pod hlavickou dna */}
                  <Skladacka otvorena={pickerOtvoreny}>
                    <div className="ur__picker">
                      <div className="ur__tabs ur__tabs--ikony">
                        <span><Soup size={11} /> Polievka</span>
                        <span className="ur__tabs--aktivny"><Utensils size={11} /> Hlavné jedlo</span>
                        <span><Coffee size={11} /> Nápoj</span>
                        <span><Leaf size={11} /> Príloha</span>
                        <span><Candy size={11} /> Dezert</span>
                        <X size={13} className="ur__muted ur__push" />
                      </div>
                      <div className="ur__picker-search">
                        <div className="ur__input">
                          {po("hladaj") ? <>Bry<i className="ur__caret" /></> : <span className="ur__placeholder">Hľadaj jedlo…<i className="ur__caret" /></span>}
                        </div>
                        <span className="ur__search"><Search size={12} /> Filtrovať podľa tagu...</span>
                      </div>
                      <div className="ur__picker-list">
                        <span className="ur__picker-new"><Plus size={11} /> Pridať nové jedlo</span>
                        <span ref={ciele.halusky} className="ur__picker-item">
                          Bryndzové halušky <i className="ur__badge-nove">Nové</i>
                        </span>
                        {!po("hladaj") && (
                          <>
                            <span className="ur__picker-item">Kurací rezeň <i className="ur__muted">Naposledy: 13. 10. 2026</i></span>
                            <span className="ur__picker-item">Hovädzí guláš <i className="ur__muted">Naposledy: 13. 10. 2026</i></span>
                          </>
                        )}
                      </div>
                    </div>
                  </Skladacka>
                  <div className="ur__dsec-cat">Polievky</div>
                  <RiadokMenu kod="A" nazov="Fazuľová polievka" cena="1.50" />
                  <div className="ur__dsec-cat">Hlavné jedlá</div>
                  <RiadokMenu kod="1" nazov="Segedínsky guláš" cena="6.90" />
                  <RiadokMenu kod="2" nazov="Pečené kuracie stehno" cena="7.20" />
                  <Skladacka otvorena={po("halusky")}>
                    <RiadokMenu kod="3" nazov="Bryndzové halušky" cena="7.20" nove />
                  </Skladacka>
                </div>
                <div className="ur__dsec">
                  <div className="ur__dsec-head">
                    <b>Štvrtok&nbsp;&nbsp;•&nbsp;&nbsp;15.10.2026</b>
                    <span className="ur__btn ur__btn--outline ur__btn--male"><Plus size={11} /> Pridať</span>
                  </div>
                  <div className="ur__dsec-cat">Polievky</div>
                  <RiadokMenu kod="A" nazov="Kapustnica" cena="1.50" />
                </div>
              </div>
              <div className="ur__builder-foot">
                {menuUlozene && <span className="ur__msg-ok">Zmeny boli uložené.</span>}
                <span ref={ciele.zavriet} className={`ur__btn ur__btn--outline ur__push ${po("zavriet") ? "ur__btn--stlacene" : ""}`}>Zavrieť</span>
                <span ref={ciele.ulozitMenu} className={`ur__btn ur__btn--primary ${po("ulozitMenu") && !menuUlozene ? "ur__btn--stlacene" : ""}`}>
                  <Save size={13} /> {po("ulozitMenu") && !menuUlozene ? "Ukladám…" : "Uložiť menu"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ============ DASHBOARD ============ */}
        <div className={`ur__screen ${obrazovka === "dashboard" ? "ur__screen--aktivna" : ""}`}>
          <div className="ur__filter">
            <span className="ur__nav"><ChevronLeft size={14} /></span>
            <span className="ur__field"><small>Dátum</small>13.10.2026</span>
            <span className="ur__nav"><ChevronRight size={14} /></span>
            <span className="ur__clock"><Clock size={13} /> 10:42:07</span>
            <span ref={ciele.nova} className={`ur__btn ur__btn--primary ${po("klikNova") && !po("modal") ? "ur__btn--stlacene" : ""}`}>
              <Plus size={13} /> Nová objednávka
            </span>
          </div>

          <div className="ur__panel ur__limits">
            <div className="ur__panel-title">Predajnosť a limity <ChevronDown size={13} /></div>
            <div className="ur__limits-cols">
              <div>
                <div className="ur__limits-head"><b>Denné menu</b><span>Objednané</span><span>Vydané</span><span>Limit</span></div>
                <div className="ur__limits-cat">Polievky</div>
                <div className="ur__limits-row">
                  <span><b className="ur__code ur__code--svetly">A</b> Paradajková polievka</span>
                  <span>18</span><span>9</span><span className="ur__limit-input ur__limit-input--prazdny">∞<i /></span>
                </div>
                <div className="ur__limits-cat">Hlavné jedlá</div>
                {[["1", "Kurací rezeň", ulozene ? 15 : 14, 6, 30, ulozene], ["2", "Hovädzí guláš", ulozene ? 12 : 11, 5, 25, ulozene], ["3", "Zeleninové rizoto", 9, 4, 20]].map(([kod, nazov, obj, vyd, lim, zmena]) => (
                  <div key={kod} className="ur__limits-row">
                    <span><b className="ur__code ur__code--svetly">{kod}</b> {nazov}</span>
                    <span className={zmena ? "ur__zmena" : ""} key={`${kod}${obj}`}>{obj}</span>
                    <span>{vyd}</span>
                    <span className="ur__limit-input">{lim}<i style={{ "--plne": `${(obj / lim) * 100}%` }} /></span>
                  </div>
                ))}
              </div>
              <div>
                <div className="ur__limits-head"><b>Stále menu</b><span>Objednané</span><span>Vydané</span><span>Limit</span></div>
                <div className="ur__limits-cat">Hlavné jedlá</div>
                <div className="ur__limits-row">
                  <span><b className="ur__code ur__code--svetly">4</b> Vyprážaný syr, hranolky</span>
                  <span>5</span><span>2</span><span className="ur__limit-input ur__limit-input--prazdny">∞<i /></span>
                </div>
                <div className="ur__limits-cat">Nápoje</div>
                <div className="ur__limits-row">
                  <span><b className="ur__code ur__code--svetly">5</b> Minerálka 0,5 l</span>
                  <span>7</span><span>3</span><span className="ur__limit-input ur__limit-input--prazdny">∞<i /></span>
                </div>
              </div>
            </div>
          </div>

          <div className="ur__panel">
            <div className="ur__orders-head">
              <h3>Objednávky</h3>
              <span className="ur__muted">3/{ulozene ? 10 : 9} doručených</span>
              <RefreshCw size={13} className="ur__muted" />
              <span className="ur__count">{ulozene ? 10 : 9}</span>
              <SlidersHorizontal size={13} className="ur__muted ur__push" />
            </div>
            <div className="ur__selects">
              <span className="ur__field"><small>Stav</small>Všetky <ChevronDown size={11} /></span>
              <span className="ur__field"><small>Trasa</small>Všetky trasy <ChevronDown size={11} /></span>
              <span className="ur__field"><small>Zdroj</small>Všetky <ChevronDown size={11} /></span>
              <span className="ur__search"><Search size={12} /> Hľadať meno, adresu, telefón...</span>
            </div>
            {/* zoradene podla stavu: Prijate, Na trase, Dorucene */}
            <KartaDashboard o={STV} stavKluc="ACCEPTED" />
            <Skladacka otvorena={ulozene}>
              <KartaDashboard
                o={BOD}
                stavKluc={naTrase ? "DELIVERY" : "ACCEPTED"}
                naTraseRef={ciele.naTrase}
                stlacene={naTrase && !po("kPrehlad")}
              />
            </Skladacka>
            <KartaDashboard o={OBU} stavKluc="DELIVERY" />
            <KartaDashboard o={DOM} stavKluc="DELIVERED" platba="Hotovosť" />
          </div>

          {/* OrderModal -- zmenseny */}
          <div className={`ur__overlay ${modalObjednavka ? "ur__overlay--tu" : ""}`}>
            <div className="ur__modal ur__modal--objednavka">
              <div className="ur__modal-title">Nová objednávka <X size={15} /></div>
              <div className="ur__zones">
                <div className="ur__zone">
                  <div className="ur__zone-title"><span>1</span> Zákazník</div>
                  <div className="ur__input">
                    <Search size={13} />
                    {po("zakaznik")
                      ? BOD.meno
                      : po("pis") ? <>Bell<i className="ur__caret" /></> : <span className="ur__placeholder">Meno, telefón, adresa…<i className="ur__caret" /></span>}
                  </div>
                  <div className={`ur__results ${po("vysledky") && !po("zakaznik") ? "ur__results--tu" : ""}`}>
                    <div ref={ciele.zakaznik} className="ur__result">
                      <b>{BOD.meno}</b>
                      <span><b className="ur__code">BOD</b> {BOD.adresa}</span>
                    </div>
                  </div>
                  {po("zakaznik") && (
                    <div className="ur__chosen">
                      <span><b className="ur__code">BOD</b> {BOD.adresa}</span>
                      <span className="ur__muted">{BOD.telefon}</span>
                      <span className="ur__muted">Saldo: —</span>
                    </div>
                  )}
                </div>

                <div className="ur__zone">
                  <div className="ur__zone-title"><span>2</span> Jedlá</div>
                  <div className="ur__tabs">
                    <span>Polievky</span><span className="ur__tabs--aktivny">Hlavné jedlá</span><span>Nápoje</span>
                  </div>
                  {MENU_POLOZKY.map((m) => {
                    const qty = m.kod === "1" ? qtyA : m.kod === "2" ? qtyB : 0;
                    return (
                      <div key={m.kod} className="ur__menu-item">
                        <b className="ur__code ur__code--svetly">{m.kod}</b>
                        <span className="ur__menu-name">{m.nazov}</span>
                        <span className="ur__muted">{m.cena.toFixed(2)} €</span>
                        <span className="ur__qty">
                          <Minus size={11} />
                          <b className={qty ? "ur__zmena" : ""} key={qty}>{qty}</b>
                          <span ref={m.kod === "1" ? ciele.plusA : m.kod === "2" ? ciele.plusB : undefined}><Plus size={11} /></span>
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="ur__zone ur__zone--sum">
                  <div className="ur__zone-title"><span>3</span> Doručenie &amp; Sumár</div>
                  <div className="ur__sum-row"><span>Zákazník</span><b>{po("zakaznik") ? BOD.meno : "—"}</b></div>
                  <div className="ur__sum-row"><span>Adresa</span><b>{po("zakaznik") ? BOD.adresa : "—"}</b></div>
                  <div className="ur__sum-row"><span>Trasa</span>{po("zakaznik") ? <Trasa nazov="SEVER" /> : <b>—</b>}</div>
                  <div className="ur__sum-row"><span>Medzisúčet</span><b>{medzisucet.toFixed(2)} €</b></div>
                  <div className="ur__sum-total"><span>Celkom na úhradu</span><b>{medzisucet.toFixed(2)} €</b></div>
                  <div ref={ciele.zadat} className={`ur__submit ${po("zadat") ? "ur__btn--stlacene" : ""}`}>
                    <CheckCircle2 size={14} /> Zadať objednávku
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ============ PREHLAD ============ */}
        <div className={`ur__screen ${obrazovka === "prehlad" ? "ur__screen--aktivna" : ""}`}>
          <div ref={prehladRef} className="ur__scroll" style={{ transform: `translateY(${-posun}px)` }}>
            <div className="ur__filter">
              <span className="ur__nav"><ChevronLeft size={14} /></span>
              <span className="ur__field ur__field--siroke"><small>Obdobie</small>Od 13.10.2026 – Do 13.10.2026</span>
              <span className="ur__nav"><ChevronRight size={14} /></span>
              <span className="ur__field"><small>Trasa</small><i className="ur__dot" /> Všetky trasy <ChevronDown size={11} /></span>
            </div>

            <div className="ur__kpis">
              {kpi.map(({ Icon, hodnota, pocet, ks, popis, mod, zmena }) => (
                <div key={popis} className={`ur__kpi ${mod ? `ur__kpi--${mod}` : ""} ${zmena ? "ur__kpi--zmena" : ""}`}>
                  <div className="ur__kpi-top">
                    <span className="ur__kpi-icon"><Icon size={15} /></span>
                    <strong>
                      {pocet !== undefined
                        ? <Pocitadlo hodnota={pocet} format={kusy} />
                        : <Pocitadlo hodnota={hodnota} format={euraBodka} />}
                      {ks !== undefined && <small> ({ks} ks)</small>}
                    </strong>
                  </div>
                  <span className="ur__kpi-label">{popis}</span>
                </div>
              ))}
            </div>

            <div className="ur__charts">
              <div className="ur__panel">
                <div className="ur__chart-head">
                  <h4>Predajnosť denného menu</h4>
                  <span className="ur__muted"><ArrowUpDown size={11} /> Najviac predávané</span>
                </div>
                <div className="ur__tabs ur__tabs--ikony">
                  <span><Soup size={11} /> Polievka</span>
                  <span className="ur__tabs--aktivny"><Utensils size={11} /> Hlavné jedlo</span>
                  <span><Coffee size={11} /> Nápoj</span>
                  <span><Leaf size={11} /> Príloha</span>
                  <span><Candy size={11} /> Dezert</span>
                </div>
                {predajnost.map((p) => (
                  <div key={p.kod} className="ur__sale">
                    <div className="ur__sale-top">
                      <span><b>{p.kod}</b> {p.nazov}</span>
                      <span className={p.zmena ? "ur__zmena" : ""} key={p.ks}>{p.ks} ks / {(p.ks * p.cena).toFixed(2)} €</span>
                    </div>
                    <div className="ur__sale-bar">
                      <span className="ur__sale-track"><i style={{ width: `${(p.ks / 16) * 100}%` }} /></span>
                      <small>{p.ks} / {p.limit} limit</small>
                    </div>
                  </div>
                ))}
              </div>

              <div className="ur__panel">
                <div className="ur__chart-head"><h4>Objednávky podľa hodiny</h4></div>
                <div className="ur__hours">
                  {hodiny.map((d) => (
                    <div key={d.h} className="ur__hour">
                      <div className="ur__hour-wrap">
                        <div className={`ur__hour-label ${d.zmena ? "ur__zmena" : ""}`} style={{ bottom: `calc(${(d.obj / maxObj) * 100}% + 4px)` }}>
                          <b>{d.obj} obj.</b>
                          <span>{d.eur} €</span>
                        </div>
                        <div className="ur__hour-bar" style={{ height: `${(d.obj / maxObj) * 100}%` }} />
                      </div>
                      <span className="ur__hour-name">{d.h}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div ref={zoznamRef} className="ur__panel">
              <div className="ur__orders-head">
                <h3>Zoznam objednávok</h3>
                <span className="ur__btn ur__btn--outline ur__push"><FileDown size={12} /> Export do Excelu</span>
              </div>
              <div className="ur__selects">
                <span className="ur__field"><small>Typ platby</small>Všetky platby <ChevronDown size={11} /></span>
                <span className="ur__field"><small>Zdroj</small>Všetky <ChevronDown size={11} /></span>
                <span className="ur__field"><small>Spôsob</small>Všetky <ChevronDown size={11} /></span>
                <span className="ur__search"><Search size={12} /> Hľadať meno, adresu, telefón...</span>
              </div>
              {/* zoradene podla casu vytvorenia */}
              <KartaPrehlad o={OBU} stavKluc="DELIVERY" />
              <KartaPrehlad
                o={BOD}
                stavKluc={dorucene ? "DELIVERED" : "DELIVERY"}
                platba={dorucene ? "Dlh" : null}
                className={po("dole") ? "ur__card--zvyraznena" : ""}
              />
              <Skladacka otvorena={webova}>
                <KartaPrehlad o={NOV} stavKluc="ACCEPTED" />
              </Skladacka>
            </div>
          </div>
        </div>

        {/* ============ SPRAVA DLHOV ============ */}
        <div className={`ur__screen ${obrazovka === "dlhy" ? "ur__screen--aktivna" : ""}`}>
          <h2 className="ur__h1">Správa dlhov</h2>
          <div className="ur__panel">
            <span className="ur__search ur__search--table"><Search size={12} /> Hľadať zákazníka...</span>
            <div className="ur__table">
              <div className="ur__tr ur__th">
                <span className="ur__check" />
                <span>Meno</span><span>Priezvisko</span><span>Telefón</span><span>Adresy</span>
                <span>Aktuálny /<br />dlh</span><span>Akcie</span>
              </div>
              {dlznici.map((d) => (
                <Skladacka key={d.priezvisko} otvorena={!(d.ona && po("vyplatene"))}>
                  <div className={`ur__tr ${d.ona && po("dlhy") && !po("vyplatene") ? "ur__tr--ona" : ""}`}>
                    <span className="ur__check" />
                    <span>{d.meno}</span>
                    <span>{d.priezvisko}</span>
                    <span>{d.tel}</span>
                    <span className="ur__addr-cell"><MapPin size={11} /> <b>{d.kod}</b> – {d.adresa}</span>
                    <b className="ur__debt">{d.dlh}</b>
                    <span>
                      <span
                        ref={d.ona ? ciele.prijat : undefined}
                        className={`ur__btn ur__btn--zeleny ${d.ona && po("klikPrijat") && !po("platbaModal") ? "ur__btn--stlacene" : ""}`}
                      >
                        <Banknote size={12} /> Prijať platbu
                      </span>
                    </span>
                  </div>
                </Skladacka>
              ))}
            </div>
          </div>

          {/* DebtPaymentModal */}
          <div className={`ur__overlay ${modalPlatba ? "ur__overlay--tu" : ""}`}>
            <div className="ur__modal ur__modal--platba">
              <div className="ur__modal-title">Prijať platbu <X size={15} /></div>
              <div className="ur__debt-info">
                <div><span>Zákazník</span><b>Eva Bellová</b></div>
                <div><span>Aktuálny dlh</span><b className="ur__debt">14,10 €</b></div>
              </div>
              <label className="ur__label">Suma na úhradu (€)</label>
              <div className="ur__input ur__input--suma">14.10</div>
              <div className="ur__modal-buttons">
                <span className="ur__btn ur__btn--outline">Zrušiť</span>
                <span ref={ciele.zaevidovat} className={`ur__btn ur__btn--primary ${po("zaevidovat") ? "ur__btn--stlacene" : ""}`}>
                  <Banknote size={13} /> Zaevidovať platbu
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function UkazkaRestauracia() {
  const rootRef = useRef(null);
  const ramRef = useRef(null);
  const scenaRef = useRef(null);
  const prehladRef = useRef(null);
  const zoznamRef = useRef(null);
  const ciele = {
    pridatJedlo: useRef(null),
    alergen1: useRef(null),
    alergen7: useRef(null),
    ulozitJedlo: useRef(null),
    menuMenu: useRef(null),
    zostavit: useRef(null),
    pridatDen: useRef(null),
    halusky: useRef(null),
    ulozitMenu: useRef(null),
    zavriet: useRef(null),
    menuDashboard: useRef(null),
    nova: useRef(null),
    zakaznik: useRef(null),
    plusA: useRef(null),
    plusB: useRef(null),
    zadat: useRef(null),
    naTrase: useRef(null),
    menuPrehlad: useRef(null),
    menuDlhy: useRef(null),
    prijat: useRef(null),
    zaevidovat: useRef(null),
  };

  const { kolo, krok, po, poPre, skocNa } = useScenar(rootRef, KROKY, DLZKA_KOLA);
  const mierka = useMierka(ramRef, SIRKA);

  // kurzor: ciel podla kroku, poloha z layoutu sceny
  const cielKurzora =
    po("kZaevidovat") ? ciele.zaevidovat :
    po("kPrijat") ? ciele.prijat :
    po("kDlhy") ? ciele.menuDlhy :
    po("kPrehlad") ? ciele.menuPrehlad :
    po("kNaTrase") ? ciele.naTrase :
    po("kZadat") ? ciele.zadat :
    po("kB") ? ciele.plusB :
    po("kA") ? ciele.plusA :
    po("kZakaznik") ? ciele.zakaznik :
    po("kNova") ? ciele.nova :
    po("kDashboard") ? ciele.menuDashboard :
    po("kZavriet") ? ciele.zavriet :
    po("kUlozitMenu") ? ciele.ulozitMenu :
    po("kHalusky") ? ciele.halusky :
    po("kPridatDen") ? ciele.pridatDen :
    po("kZostavit") ? ciele.zostavit :
    po("kMenu") ? ciele.menuMenu :
    po("kUlozitJedlo") ? ciele.ulozitJedlo :
    po("kAlergen7") ? ciele.alergen7 :
    po("kAlergen1") ? ciele.alergen1 :
    po("kPridat") ? ciele.pridatJedlo : null;
  const [kurzor, setKurzor] = useState(null);
  const [posun, setPosun] = useState(0);
  // koniec kola aj skok na kapitolu: predosly zaber sa rozplynie do noveho
  const odchadza = usePrelinanie(kolo, krok, 0, posun);

  useLayoutEffect(() => {
    const p = polohaV(cielKurzora?.current, scenaRef.current);
    if (p) setKurzor({ x: p.x + p.w * 0.6, y: p.y + p.h * 0.6 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [krok, kolo]);

  // Prehlad sa "posunie" k zoznamu objednavok
  useLayoutEffect(() => {
    const p = polohaV(zoznamRef.current, prehladRef.current);
    setPosun(po("dole") && p ? p.y - 12 : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [krok, kolo]);

  const klik =
    (po("klikPridat") && !po("jedloModal")) ||
    (po("alergen1") && !po("kAlergen7")) ||
    (po("alergen7") && !po("kUlozitJedlo")) ||
    (po("ulozitJedlo") && !po("jedloUlozene")) ||
    (po("menu") && !po("kZostavit")) ||
    (po("klikZostavit") && !po("builder")) ||
    (po("klikPridatDen") && !po("picker")) ||
    (po("halusky") && !po("kUlozitMenu")) ||
    (po("ulozitMenu") && !po("ulozeneMenu")) ||
    (po("zavriet") && !po("kDashboard")) ||
    (po("dashboard") && !po("kNova")) ||
    (po("klikNova") && !po("modal")) ||
    (po("zakaznik") && !po("kA")) ||
    (po("klikA") && !po("kB")) ||
    (po("klikB") && !po("kZadat")) ||
    (po("zadat") && !po("ulozene")) ||
    (po("naTrase") && !po("kPrehlad")) ||
    (po("prehlad") && !po("webova")) ||
    (po("dlhy") && !po("kPrijat")) ||
    (po("klikPrijat") && !po("kZaevidovat")) ||
    (po("zaevidovat") && !po("vyplatene"));

  return (
    <div ref={rootRef} className="ur">
      <div ref={ramRef} className="ur__ram" style={{ height: VYSKA * mierka }} aria-hidden="true">
        <div
          ref={scenaRef}
          className="ur__scena"
          style={{ width: SIRKA, height: VYSKA, transform: `scale(${mierka})` }}
        >
          <div className="ur__bar"><span /><span /><span /></div>
          <div className="ur__header">
            <span className="ur__logo">Rozvozy</span>
            <span className="ur__user"><User size={13} /> Manažér</span>
          </div>

          <div className="ur__vrstvy">
            <Aplikacia key={kolo} po={po} ciele={ciele} prehladRef={prehladRef} zoznamRef={zoznamRef} posun={posun} />
            {odchadza && (
              <Aplikacia key={`k${odchadza.kolo}`} po={poPre(odchadza.krok)} posun={odchadza.extra ?? 0} className="ur__stary" />
            )}
          </div>

          <MousePointer2
            size={18}
            className={[
              "ur__cursor",
              cielKurzora && kurzor ? "ur__cursor--tu" : "",
              klik ? "ur__cursor--klik" : "",
            ].join(" ")}
            style={kurzor ? { "--x": `${kurzor.x}px`, "--y": `${kurzor.y}px` } : undefined}
          />
        </div>
      </div>

      <Kapitoly kapitoly={KAPITOLY} po={po} skocNa={skocNa} />
    </div>
  );
}

export default UkazkaRestauracia;
